import { useId } from "react";
import { cn } from "@fmc-digital/ui";
import {
  getNpsRatingBucket,
  npsRatingOptionStyles,
  npsRatingScaleLabelsStyles,
  npsRatingScaleMobileHighLabelRowStyles,
  npsRatingScaleMobileHighLabelStyles,
  npsRatingScaleMobileLabelRowStyles,
  npsRatingScaleMobileLowLabelRowStyles,
  npsRatingScaleMobileLowLabelStyles,
  npsRatingScaleMobileRootStyles,
  npsRatingScaleMobileRowStyles,
} from "./nps-rating-scale.styles";

const NPS_RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export interface NpsRatingScaleProps {
  value: number | null;
  onChange: (value: number) => void;
  ariaLabel: string;
  lowLabel: string;
  highLabel: string;
  className?: string;
}

interface NpsRatingOptionProps {
  rating: number;
  value: number | null;
  name: string;
  inputId: string;
  onChange: (value: number) => void;
}

function NpsRatingOption({
  rating,
  value,
  name,
  inputId,
  onChange,
}: Readonly<NpsRatingOptionProps>) {
  const isSelected = value === rating;
  const bucket = getNpsRatingBucket(rating);

  return (
    <label
      htmlFor={inputId}
      data-testid={`nps-rating-option-${rating}`}
      data-bucket={bucket}
      className={cn(
        npsRatingOptionStyles({
          layout: "responsive",
          bucket,
          selected: isSelected,
        }),
      )}
    >
      <input
        type="radio"
        id={inputId}
        name={name}
        value={rating}
        checked={isSelected}
        onChange={() => onChange(rating)}
        className="sr-only"
        aria-label={`Rating ${rating} out of 10`}
      />
      {rating}
    </label>
  );
}

export function NpsRatingScale({
  value,
  onChange,
  ariaLabel,
  lowLabel,
  highLabel,
  className,
}: Readonly<NpsRatingScaleProps>) {
  const groupId = useId();
  const name = `${groupId}-nps-rating`;

  return (
    <fieldset
      className={cn("m-0 min-w-0 w-full border-0 p-0", className)}
      data-testid="nps-rating-scale"
    >
      <legend className="sr-only">{ariaLabel}</legend>

      <div className={cn(npsRatingScaleMobileRootStyles())} data-testid="nps-rating-scale-mobile">
        <div
          className={cn(npsRatingScaleMobileLabelRowStyles(), npsRatingScaleMobileLowLabelRowStyles())}
          data-testid="nps-rating-scale-low-label-row"
        >
          <span
            className={cn(npsRatingScaleMobileLowLabelStyles())}
            data-testid="nps-rating-scale-low-label"
          >
            {lowLabel}
          </span>
        </div>
      </div>

      <div className={cn("w-full md:overflow-x-auto md:pb-1")}>
        <div
          className={cn(
            npsRatingScaleMobileRowStyles(),
            "gap-y-4",
            "md:mx-auto md:flex md:min-w-max md:flex-row md:items-center md:justify-center md:gap-2 md:gap-y-0",
          )}
          data-testid="nps-rating-scale-row"
        >
          {NPS_RATINGS.map((rating) => (
            <NpsRatingOption
              key={rating}
              rating={rating}
              value={value}
              name={name}
              inputId={`${groupId}-rating-${rating}`}
              onChange={onChange}
            />
          ))}
        </div>
      </div>

      <div className={cn(npsRatingScaleMobileRootStyles())}>
        <div
          className={cn(npsRatingScaleMobileLabelRowStyles(), npsRatingScaleMobileHighLabelRowStyles())}
          data-testid="nps-rating-scale-high-label-row"
        >
          <span
            className={cn(npsRatingScaleMobileHighLabelStyles())}
            data-testid="nps-rating-scale-high-label"
          >
            {highLabel}
          </span>
        </div>
      </div>

      <div className="hidden md:block" data-testid="nps-rating-scale-desktop">
        <div className={cn(npsRatingScaleLabelsStyles())}>
          <span>{lowLabel}</span>
          <span className="text-right">{highLabel}</span>
        </div>
      </div>
    </fieldset>
  );
}
